import type {
  PlayerGameRound,
  PlayerGameRoundDetailResponse,
} from "@utopia-well-done-ltd/contracts";
import { useEffect, useRef, useState } from "react";
import { ApiError, api } from "../api/client.js";
import { asAmount, asMoment } from "../format.js";
import { BlackjackRound } from "./BlackjackRound.js";

const money = (amount: string | null, currency: string) =>
  amount === null ? "—" : asAmount({ amount, currency });

function RoundDetail({
  detail,
  onClose,
}: {
  detail: PlayerGameRoundDetailResponse;
  onClose: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    if (dialog.current?.showModal) dialog.current.showModal();
    else dialog.current?.setAttribute("open", "");

    document.body.classList.add("modal-open");
    return () => document.body.classList.remove("modal-open");
  }, []);

  return (
    <dialog
      aria-labelledby="round-detail-title"
      className="round-dialog"
      onCancel={onClose}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") onClose();
      }}
      ref={dialog}
    >
      <div className="round-dialog-head">
        <div>
          <h2 className="heading-20" id="round-detail-title">
            Round detail
          </h2>
          <p className="mono-12 faint">{detail.round.id}</p>
        </div>
        <button
          aria-label="Close round detail"
          className="btn btn-quiet btn-sm"
          onClick={onClose}
          type="button"
        >
          Close
        </button>
      </div>

      <div className="round-dialog-body">
        <div className="round-summary">
          <div className="fact">
            <div className="fact-label">Integrity</div>
            <div className="fact-value">
              {detail.verification.verified
                ? "Verified"
                : detail.verification.reason === "not_settled"
                  ? "Pending settlement"
                  : `Failed: ${detail.verification.reason ?? "unknown"}`}
            </div>
          </div>
          <div className="fact">
            <div className="fact-label">Game</div>
            <div className="fact-value">{detail.round.game}</div>
          </div>
          <div className="fact">
            <div className="fact-label">Stake</div>
            <div className="fact-value mono-12">
              {money(detail.round.stake, detail.round.currency)}
            </div>
          </div>
          <div className="fact">
            <div className="fact-label">Payout</div>
            <div className="fact-value mono-12">
              {money(detail.round.payout, detail.round.currency)}
            </div>
          </div>
          <div className="fact">
            <div className="fact-label">Returned stake</div>
            <div className="fact-value mono-12">
              {money(detail.round.returned, detail.round.currency)}
            </div>
          </div>
          <div className="fact">
            <div className="fact-label">Engine</div>
            <div className="fact-value mono-12">{detail.round.engineVersion}</div>
          </div>
          <div className="fact">
            <div className="fact-label">Ruleset</div>
            <div className="fact-value mono-12">{detail.round.rulesetId ?? "—"}</div>
          </div>
          <div className="fact">
            <div className="fact-label">Table</div>
            <div className="fact-value mono-12">{detail.round.tableId ?? "—"}</div>
          </div>
        </div>

        {detail.round.game === "blackjack" ? (
          <BlackjackRound terminalState={detail.round.terminalState} />
        ) : null}

        <div className="round-actions-head">
          <span className="label-14-strong">Action timeline</span>
          <span className="label-13 faint">{detail.actions.length} recorded</span>
        </div>
        {detail.actions.length === 0 ? (
          <p className="empty">No recorded player actions.</p>
        ) : (
          <div className="action-timeline">
            {detail.actions.map((action) => (
              <div className="action-event" key={action.sequence}>
                <span className="action-sequence">{action.sequence}</span>
                <div>
                  <strong>{action.action.replaceAll("_", " ")}</strong>
                  <span className="label-13 dim">{asMoment(action.createdAt)}</span>
                </div>
                <span
                  className={`badge ${action.accepted === false ? "badge-banned" : "badge-active"}`}
                >
                  {action.accepted === null ? "legacy" : action.accepted ? "accepted" : "rejected"}
                </span>
                {action.payload === null ? null : (
                  <details className="technical-details">
                    <summary>Technical details</summary>
                    <pre>{JSON.stringify(action.payload, null, 2)}</pre>
                  </details>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </dialog>
  );
}

export function GameHistory({ playerId }: { playerId: string }) {
  const [rounds, setRounds] = useState<PlayerGameRound[] | null>(null);
  const [detail, setDetail] = useState<PlayerGameRoundDetailResponse | null>(null);
  const [loadingRound, setLoadingRound] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const detailGeneration = useRef(0);

  useEffect(() => {
    const controller = new AbortController();
    detailGeneration.current += 1;
    api
      .playerGameRounds(playerId, controller.signal)
      .then(({ rounds: found }) => setRounds(found))
      .catch((caught: unknown) => {
        if (!controller.signal.aborted) {
          setError(caught instanceof ApiError ? caught.message : "Could not load game history.");
        }
      });
    return () => {
      detailGeneration.current += 1;
      controller.abort();
    };
  }, [playerId]);

  async function open(roundId: string) {
    const requestGeneration = ++detailGeneration.current;
    setError(null);
    setLoadingRound(roundId);
    try {
      const found = await api.playerGameRound(playerId, roundId);
      if (detailGeneration.current === requestGeneration) setDetail(found);
    } catch (caught) {
      if (detailGeneration.current !== requestGeneration) return;
      setError(caught instanceof ApiError ? caught.message : "Could not load the round.");
    } finally {
      if (detailGeneration.current === requestGeneration) setLoadingRound(null);
    }
  }

  if (error)
    return (
      <p className="banner" role="alert">
        {error}
      </p>
    );
  if (!rounds) return <p className="empty">Loading…</p>;
  if (rounds.length === 0) return <p className="empty">This player has not played a round.</p>;

  return (
    <div className="game-history">
      <div className="game-history-head">
        <div>
          <div className="label-14-strong">Game history</div>
          <div className="label-13 faint">Newest rounds first</div>
        </div>
        <span className="label-13 dim">{rounds.length} rounds</span>
      </div>
      <div className="table-scroll">
        <table className="games-table mobile-cards">
          <thead>
            <tr>
              <th>Started</th>
              <th>Game</th>
              <th>Status</th>
              <th>Stake</th>
              <th>Payout</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rounds.map((round) => (
              <tr key={round.id}>
                <td className="label-13 dim" data-label="Started">
                  {asMoment(round.startedAt)}
                </td>
                <td className="label-13" data-label="Game">
                  {round.game}
                </td>
                <td data-label="Status">
                  <span className={`badge badge-${round.status}`}>
                    <span className="dot" />
                    {round.status}
                  </span>
                </td>
                <td className="mono-12" data-label="Stake">
                  {money(round.stake, round.currency)}
                </td>
                <td className="mono-12" data-label="Payout">
                  {money(round.payout, round.currency)}
                </td>
                <td data-label="Action">
                  <span className="row-actions">
                    <button
                      className="btn btn-quiet btn-sm"
                      disabled={loadingRound === round.id}
                      onClick={() => void open(round.id)}
                      type="button"
                    >
                      {loadingRound === round.id ? "Loading…" : "Inspect"}
                    </button>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detail ? (
        <RoundDetail
          detail={detail}
          onClose={() => {
            detailGeneration.current += 1;
            setDetail(null);
            setLoadingRound(null);
          }}
        />
      ) : null}
    </div>
  );
}
